import React, { useState } from 'react';
import { Heart, X, RotateCcw, Eye } from 'lucide-react';

// ============================================
// BlindMode — Who they are, not what they look like
// ============================================
// No photos shown initially. Users see:
// - Interests, personality traits, values
// - Bio (if available)
// - Custom answers from the questions system
// - Age range and city (broad)
//
// Tap "Curious" to reveal photos.
// This flips the dating app paradigm — connection
// starts with substance, not appearance.
// ============================================

export default function BlindMode({ profiles, currentIndex, user, onSwipe, onMaybe }) {
  var [revealedPhotos, setRevealedPhotos] = useState({});
  var [cardIndex, setCardIndex] = useState(0);

  var p = profiles[cardIndex];
  if (!p) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
        <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>🫣</span>
        <p style={{ fontSize: 15, fontWeight: 600 }}>No profiles to discover right now</p>
        <p style={{ fontSize: 13 }}>Check back soon — new people join every day</p>
      </div>
    );
  }

  var isRevealed = revealedPhotos[p.userId];
  var hasPhotos = p.photos && p.photos.length > 0;

  function handleAction(action) {
    if (action === 'pass') onSwipe('left', cardIndex);
    else if (action === 'like') onSwipe('right', cardIndex);
    else if (action === 'maybe') onMaybe(cardIndex);

    // Move to next card
    setCardIndex(function(prev) { return prev + 1; });
  }

  return (
    <div style={{ padding: '0 16px 100px', maxWidth: 440, margin: '0 auto' }}>
      {/* The blind profile card */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 20,
        overflow: 'hidden', marginBottom: 16,
      }} role="article" aria-label={'Profile: ' + (isRevealed ? p.firstName : 'Anonymous') + (p.age ? ', age ' + p.age : '')}>
        {/* Photo area — blurred or hidden */}
        <div style={{
          height: isRevealed ? 360 : 180, position: 'relative', overflow: 'hidden',
          background: 'linear-gradient(135deg, var(--primary) 0%, #8b5cf6 100%)',
          transition: 'height 0.4s ease',
        }}>
          {isRevealed && hasPhotos ? (
            <img src={p.photos[0].url} alt={p.firstName} style={{
              width: '100%', height: '100%', objectFit: 'cover',
            }} />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.9)',
            }}>
              <span style={{ fontSize: 48, marginBottom: 8 }}>🫣</span>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Blind mode</div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>Get to know who they are first</div>
            </div>
          )}

          {/* Reveal button */}
          {!isRevealed && hasPhotos && (
            <button onClick={() => setRevealedPhotos(function(prev) {
              var next = { ...prev }; next[p.userId] = true; return next;
            })} style={{
              position: 'absolute', bottom: 12, right: 12,
              padding: '8px 16px', borderRadius: 20, border: 'none',
              background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
              color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Eye size={14} /> Curious? Reveal photos
            </button>
          )}

          {/* Name overlay */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, padding: '30px 18px 14px',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>
              {isRevealed ? p.firstName : 'Someone'}{p.age ? ', ' + p.age : ''}
            </div>
            {p.location?.city && (
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>📍 {p.location.city}</div>
            )}
          </div>
        </div>

        {/* The substance — what makes this mode different */}
        <div style={{ padding: '18px 18px 10px' }}>
          {/* Bio */}
          {p.bio && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.7 }}>
                "{p.bio}"
              </div>
            </div>
          )}

          {/* Interests — the meat of it */}
          {p.interests && p.interests.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Into
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {p.interests.map(function(int, i) {
                  return (
                    <span key={i} style={{
                      padding: '6px 14px', borderRadius: 16, fontSize: 13, fontWeight: 500,
                      background: 'var(--surface-alt)', color: 'var(--text-primary)',
                    }}>{int}</span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Personality traits (if available from profile) */}
          {p.personalityType && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Personality
              </div>
              <div style={{
                padding: '10px 14px', borderRadius: 12, fontSize: 13, lineHeight: 1.6,
                background: 'rgba(99,102,241,0.06)', color: 'var(--text-primary)',
                borderLeft: '3px solid var(--primary)',
              }}>
                {p.personalityType}
              </div>
            </div>
          )}

          {/* Occupation + Education */}
          {(p.occupation || p.education) && (
            <div style={{ marginBottom: 14, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              {p.occupation && <div>💼 {p.occupation}</div>}
              {p.education && <div>🎓 {p.education}</div>}
            </div>
          )}

          {/* What they're looking for */}
          {p.lookingFor && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Looking for
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                {p.lookingFor}
              </div>
            </div>
          )}

          {/* Prompt answers / custom Q&A */}
          {p.prompts && p.prompts.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {p.prompts.map(function(prompt, i) {
                return (
                  <div key={i} style={{
                    marginBottom: 10, padding: '12px 14px', borderRadius: 12,
                    background: 'var(--surface-dim)', border: '1px solid var(--border)',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', marginBottom: 4 }}>
                      {prompt.question || prompt.prompt}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                      {prompt.answer || prompt.response}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* The blind mode philosophy note */}
          {!isRevealed && (
            <div style={{
              padding: '10px 14px', borderRadius: 10, fontSize: 12,
              background: 'rgba(139,92,246,0.06)', color: 'var(--text-secondary)',
              lineHeight: 1.6, textAlign: 'center', marginTop: 8,
            }}>
              You're seeing who they are, not what they look like. When connection starts here, it starts with substance.
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, padding: '8px 0' }} role="group" aria-label="Profile actions">
        <button onClick={() => handleAction('pass')} disabled={!user?.isIdentityVerified} style={{
          width: 64, height: 64, borderRadius: '50%', border: '3px solid #ff6b6b',
          background: 'var(--surface)', color: '#ff6b6b', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: user?.isIdentityVerified ? 1 : 0.5,
        }} aria-label="Pass on this profile"><X size={28} strokeWidth={3} /></button>

        <button onClick={() => handleAction('maybe')} disabled={!user?.isIdentityVerified} style={{
          width: 52, height: 52, borderRadius: '50%', border: '2px solid #fbbf24',
          background: 'var(--surface)', color: '#fbbf24', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: user?.isIdentityVerified ? 1 : 0.5,
        }} aria-label="Maybe — see again later"><RotateCcw size={22} strokeWidth={2.5} /></button>

        <button onClick={() => handleAction('like')} disabled={!user?.isIdentityVerified} style={{
          width: 64, height: 64, borderRadius: '50%', border: '3px solid #51cf66',
          background: 'var(--surface)', color: '#51cf66', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: user?.isIdentityVerified ? 1 : 0.5,
        }} aria-label="Like this profile"><Heart size={28} strokeWidth={3} /></button>
      </div>

      {/* Card counter */}
      <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
        {cardIndex + 1} of {profiles.length}
      </div>
    </div>
  );
}
